import { Fragment, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';

import { Button } from '@components/button';
import { FormLabel } from '@components/form-label';
import { Select } from '@components/select';
import { SelectMulti } from '@components/selectmulti';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { TextField } from '@components/text-field';
import { TextArea } from '@components/textarea';

import { FileUpload } from 'components/FileUpload';
import { FileUploadAnother } from 'components/FileUploadAnother';
import { Loading } from 'components/Loading';
import { Modal } from 'features/modals';
import { Application } from 'models/applications';
import { useStore } from 'models/Provider';

import { prepData } from './utils';

function AddApplicantModal({ job, closeModal }) {
  const { t } = useStore();
  const [cv, setCv] = useState(null);
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [coverLetter, setCoverLetter] = useState(null);
  const [language, setLanguage] = useState(job.defaultLanguageData.language);
  const [application, setApplication] = useState(() =>
    Application.create({ form: getSnapshot(job.defaultLanguageData.form), id: -1, language })
  );

  useEffect(() => {
    const newForm = getSnapshot(job.languages.find((obj) => obj.language === language).form);
    const newApp = Application.create({ form: newForm, id: -1, language });
    newApp.setName(application.name);
    newApp.setEmail(application.email);
    newApp.setSsn(application.ssn);
    newApp.setPhone(application.phone);

    application.form.sections.forEach((section) => {
      section.questions.forEach((question) => {
        newApp.form.sections.forEach((nfs) => {
          nfs.questions.forEach((nfq) => {
            if (nfq.id === question.id && question.value) {
              if (question.type === 'multidropdown' && question.value.length > 0) {
                const newVal = question.value.reduce((res, item) => {
                  res[item] = true;
                  return res;
                }, {});
                nfq.setValue(newVal);
              } else {
                nfq.setValue(question.value);
              }
            }
          });
        });
      });
    });

    setApplication(newApp);
    // eslint-disable-next-line
  }, [language]);

  const setFile = (questionId, file, multiple = false, multipleIndex = null) => {
    setFiles((old) => {
      if (multiple) {
        let filesArray = Object.assign([], old[questionId]);
        if (multipleIndex) {
          filesArray.splice(multipleIndex, 1, file);
        } else if (file) {
          filesArray.push(file);
        }
        return { ...old, [questionId]: filesArray };
      }
      if (old[questionId] === undefined) {
        return { ...old, [questionId]: file };
      }
      const newObj = { ...old };
      delete newObj[questionId];
      return newObj;
    });
  };

  function isValid(data) {
    let err = {};
    let hasErrors = false;
    if (!data.name) {
      err.name = t('errors.field.required');
      hasErrors = true;
    }
    if (!data.email) {
      err.email = t('errors.field.required');
      hasErrors = true;
    }
    if (hasErrors) {
      setErrors(err);
      return false;
    }
    return true;
  }

  function clearError(field) {
    if (errors[field]) {
      let err = { ...errors };
      delete err[field];
      setErrors(err);
    }
  }

  function setName(val) {
    clearError('name');
    application.setName(val);
  }

  function setEmail(val) {
    clearError('email');
    application.setEmail(val);
  }

  async function submit() {
    const data = getSnapshot(application);
    if (!isValid(data)) {
      return;
    }

    setLoading(true);
    const obj = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      ssn: data.ssn,
      sections: data.form.sections,
      cv,
      coverLetter,
      language,
      files,
    };
    const form = prepData(obj);
    const res = await job.addApplicant(form);
    if (!res.error) {
      closeModal();
    } else {
      setLoading(false);
      setErrors(res.data);
    }
  }

  function getQuestionField(q) {
    let el = null;

    switch (q.type) {
      case 'attachment':
        if (q.multiple) {
          el = (
            <Stack space={1}>
              <FormLabel text={q.title} />
              {(!files[q.id] || files[q.id].length === 0) && (
                <FileUpload key={q.id} file={null} onChange={(file) => setFile(q.id, file, true)} />
              )}
              {files[q.id] && files[q.id].length > 0 && (
                <Stack space={2}>
                  {files[q.id].map((v, index) => (
                    <FileUpload
                      key={index}
                      file={v}
                      onChange={(file) => setFile(q.id, file, true, index)}
                    />
                  ))}
                  <FileUploadAnother key={q.id} onChange={(file) => setFile(q.id, file, true)} />
                </Stack>
              )}
            </Stack>
          );
        } else {
          el = (
            <Stack space={1}>
              <FormLabel text={q.title} />
              <FileUpload
                key={q.id}
                file={files[q.id] || null}
                onChange={(file) => setFile(q.id, file)}
              />
            </Stack>
          );
        }
        break;
      case 'multidropdown':
        let val = null;
        if (q.value) {
          val = q.value.reduce((res, item) => {
            res[item] = true;
            return res;
          }, {});
        }
        el = (
          <SelectMulti
            label={q.title}
            defaultValue={val}
            options={q.optionsWithLabel.slice()}
            onChange={(options) => q.setValue(options.map((option) => option.value))}
          />
        );
        break;
      case 'dropdown':
        el = (
          <Select
            label={q.title}
            defaultValue={q.value}
            options={q.optionsWithLabel.slice()}
            onChange={q.setValue}
          />
        );
        break;
      case 'multi_line':
        el = <TextArea label={q.title} value={q.value || ''} onChange={q.setValue} />;
        break;
      case 'one_line':
      default:
        el = (
          <TextField label={q.title} name={q.title} value={q.value || ''} onChange={q.setValue} />
        );
        break;
    }
    return <div key={q.id}>{el}</div>;
  }
  if (!application) return null;

  return (
    <Modal style={{ minHeight: 150 }} key={application.language}>
      {loading && <Loading />}
      <Modal.Header text={t('modals.addapplicant.header')} />
      <Stack space={4} style={{ opacity: loading ? 0.6 : 1 }}>
        {job.enabledLanguages.length > 1 && (
          <Select
            label={t('job.create.form.label.language')}
            isClearable={false}
            isSearchable={false}
            defaultValue={language}
            options={job.enabledLanguages.map((obj) => ({
              label: t(`general.${obj.language}`),
              value: obj.language,
            }))}
            onChange={setLanguage}
          />
        )}
        <TextField
          label={'* ' + t('job.create.form.label.name')}
          value={application.name || ''}
          onChange={setName}
          error={errors['name']}
        />
        <TextField
          label={'* ' + t('job.create.form.label.email')}
          value={application.email || ''}
          onChange={setEmail}
          error={errors['email']}
        />
        {job.includePhone && (
          <TextField
            label={t('job.create.form.label.phone')}
            value={application.phone || ''}
            onChange={application.setPhone}
          />
        )}
        {job.includeSsn && (
          <TextField
            label={t('job.create.form.label.ssn')}
            value={application.ssn || ''}
            onChange={application.setSsn}
          />
        )}
        <Stack space={1}>
          <FormLabel text={t('modals.addapplicant.field.cv')} />
          <FileUpload file={cv} onChange={setCv} />
        </Stack>

        <Stack space={1}>
          <FormLabel text={t('modals.addapplicant.field.cover')} />
          <FileUpload file={coverLetter} onChange={setCoverLetter} />
        </Stack>
      </Stack>

      {application.form.sections[0].questions.length > 0 &&
        application.form.sections.map((f) => {
          return (
            <Fragment key={f.id}>
              <Separator />
              <Stack space={4}>{f.questions.map(getQuestionField)}</Stack>
            </Fragment>
          );
        })}
      <Modal.Actions>
        <Button
          variant="secondary"
          isDisabled={loading}
          onClick={closeModal}
          text={t('modals.addapplicant.button.cancel')}
        />
        <Button
          isDisabled={loading}
          onClick={submit}
          text={t('modals.addapplicant.button.submit')}
        />
      </Modal.Actions>
    </Modal>
  );
}

export default observer(AddApplicantModal);
